import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components/macro';
import { useIntl } from 'react-intl';
import Slider from '../../../components/sliders/Slider/Slider';
import { useSelector } from '../../../redux';
import { loadCamsFunction } from '../CamsContextProvider';
import camsSliderSettings from '../../../helpers/constants/camsSliderSettings';
import { CameraThumbnailCard } from '../../../components/cards';
import { CamTrackingPosition } from 'src/client/constants/camTracking';
import { CameraThumbnailCardWrapper } from 'src/client/components/cards/CameraThumbnailCard';
import { useGenerateCamUrlWithParams } from 'src/client/helpers/urls/useGenerateCamUrlWithParams';

const Div = styled.div`
  &.container {
    margin-top: 25px;

    @media (max-width: 767px) {
      display: none;
    }
  }

  &:focus {
    outline: none;
  }

  ${CameraThumbnailCardWrapper} {
    margin: 5px;
    background: none;
    width: auto;
  }

  &.cams {
    max-height: 235px;
    overflow-y: hidden;
    margin-bottom: 20px;

    h3 {
      font-weight: 100;
      margin-top: 10px;
      min-height: 35px;
      line-height: 35px;
    }
  }

  .btn-view-all {
    margin-right: 10px;
    line-height: 25px;
    color: ${(props) => props.theme.primary4};

    :hover,
    :active,
    :focus {
      color: ${(props) => props.theme.primary4};
    }
  }
`;

type Props = {
  camTrackingPosition?: CamTrackingPosition;
  className?: string;
  mobileOnly?: boolean;
  settings: typeof camsSliderSettings;
  itemCount?: number;
};

export default function CamsSlider(props: Props) {
  const { camTrackingPosition, className, itemCount = 12, mobileOnly, settings } = props;
  const { cams, loading, error } = useSelector((state) => state.cams);
  const camProviders = useSelector((state) => state.app.config.camProviders);
  const siteUUID = useSelector((state) => state.site.UUID);
  const dispatch = useDispatch();
  const intl = useIntl();

  const generateCamUrlWithParams = useGenerateCamUrlWithParams({
    trackingPosition: camTrackingPosition,
    provider: cams?.[0]?.provider ?? 'streamate',
  });

  const noCams = loading || !cams || !cams.length || !cams[0].total || !!error;

  useEffect(() => {
    if (noCams) {
      dispatch(loadCamsFunction(camProviders, siteUUID));
    }
  }, [dispatch, noCams, camProviders, siteUUID]);

  if (noCams) return null;

  const { siteCams, provider } = cams[0];

  return (
    <Div className={className}>
      <Slider
        className={`container ${mobileOnly ? 'visible-xs' : ''} cams`}
        title={
          <div>
            <a
              href={generateCamUrlWithParams('/cams-list')}
              className="pull-right btn-view-all"
              target="_blank"
              rel="nofollow noopener noreferrer"
            >
              {intl.formatMessage({ id: 'cams.buttons.viewAll' })}
            </a>
            <h3>{intl.formatMessage({ id: 'cams.slider.headline' })}</h3>
          </div>
        }
        settings={settings}
      >
        {siteCams.slice(0, itemCount).map((cam) => (
          <CameraThumbnailCard
            provider={provider}
            cam={cam}
            camTrackingPosition={camTrackingPosition}
            className="card"
            eventContentType="cam_slider"
            key={cam.id}
          />
        ))}
      </Slider>
    </Div>
  );
}
